"use client";

import { useState } from "react";
import {
  Checkbox,
  Notice,
  PillGroup,
  SelectField,
  TextAreaField,
  TextField,
} from "@/components/tool-kit/primitives";
import { BUTTON_SMALL } from "@/components/tool-kit/styles";
import { MovingAverageResults } from "@/components/tools/stats-regression/moving-average-results";
import {
  useToolLocale,
  useToolNumber,
  useToolUi,
} from "@/components/tools/tool-ui";
import { t, tPlural } from "@/lib/i18n/interpolate";
import {
  parseIntegerInput,
  parseNumberInput,
  parseNumberList,
} from "@/lib/parse-number";
import type {
  Alignment,
  EmaSeed,
  MovingAverageOptions,
  MovingAverageType,
} from "@/lib/tool-kit/stats-regression/moving-average";
import { parseSeries } from "@/lib/tool-kit/stats-regression/series";

/** English strings. A translated content file overrides these through `ui`. */
export const MOVING_AVERAGE_CALCULATOR_STRINGS = {
  dataLabel: "Your series — one value per line, or “label, value” per line",
  dataHint:
    "Paste a column from a spreadsheet (a date or month in the first column and the value in the second), or a list of numbers separated by commas, spaces or new lines. A header row is skipped; blank cells are dropped.",
  placeholder: "Jan, 112\nFeb, 118\nMar, 132\nApr, 129",
  loadExample: "Load example",
  clear: "Clear",
  example:
    "Month\tPassengers\nJan 1949\t112\nFeb 1949\t118\nMar 1949\t132\nApr 1949\t129\nMay 1949\t121\nJun 1949\t135\nJul 1949\t148\nAug 1949\t148\nSep 1949\t136\nOct 1949\t119\nNov 1949\t104\nDec 1949\t118\nJan 1950\t115\nFeb 1950\t126\nMar 1950\t141\nApr 1950\t135\nMay 1950\t125\nJun 1950\t149\nJul 1950\t170\nAug 1950\t170\nSep 1950\t158\nOct 1950\t133\nNov 1950\t114\nDec 1950\t140",
  valuesRead: "one: {count} value read|other: {count} values read",
  skippedValues:
    "one: , {count} row without a number skipped|other: , {count} rows without a number skipped",
  typeLabel: "Type of moving average",
  typeSimple: "Simple (SMA)",
  typeWeighted: "Weighted (WMA)",
  typeExponential: "Exponential (EMA)",
  windowLabel: "Window size (k)",
  spanLabel: "Span (N)",
  windowHint: "How many observations each average covers.",
  alignmentLabel: "Window position",
  alignTrailing: "Trailing (the latest k values)",
  alignCentred: "Centred (around each point)",
  weightsLabel: "Weights, oldest to newest",
  weightsHint:
    "Leave blank for 1, 2, … k (the newest value counts most). Enter k weights to choose your own.",
  smoothingLabel: "Smoothing",
  smoothingSpan: "From the span: α = 2 ÷ (N + 1)",
  smoothingAlpha: "Enter α directly",
  alphaLabel: "Smoothing factor α (0 to 1)",
  seedLabel: "Start the average from",
  seedFirst: "The first observation",
  seedAverage: "The simple average of the first N",
  partial:
    "Average the points that exist at the ends instead of leaving blanks",
  invalidWindow:
    "Enter a whole-number window between 1 and the number of values ({n}).",
  invalidWeights:
    "Enter exactly {k} weights, none negative, adding up to more than zero — or leave the box blank.",
  invalidAlpha: "α must be a number above 0 and at most 1.",
  enterPrompt: "Enter at least two numbers to see a moving average.",
  results: "Results",
  observationsCard: "Observations",
  averagesCard: "Averages computed",
  latestCard: "Latest average",
  nextForecast: "Forecast for the next period",
  alphaCard: "Smoothing factor α",
  maeCard: "Mean absolute error",
  rmseCard: "Root mean squared error",
  mapeCard: "Mean absolute % error",
  accuracyNote:
    "The error figures treat each average as a one-step-ahead forecast: the average up to the previous period predicts the current value ({n} forecasts scored). A smaller window follows the data faster; a larger one smooths more but lags.",
  centredNote:
    "Centred averages use values after each point as well as before it, so they smooth a series well but cannot forecast: there is no future for them to use. They also leave blanks at both ends.",
  evenCentredNote:
    "With an even window there is no middle observation, so a simple centred average uses the classical 2×k method: half weight on the two end points of k + 1.",
  weightsUsed: "Weights used, oldest to newest: {weights}.",
  weightsSeparator: ", ",
  plotTitle: "Series and moving average",
  plotLabel: "Line chart of {n} values and their moving average",
  axisX: "Observation",
  axisY: "Value",
  seriesData: "Data",
  seriesAverage: "Moving average",
  tableTitle: "Values and averages",
  colIndex: "#",
  colLabel: "Label",
  colValue: "Value",
  colAverage: "Moving average",
  tableMore:
    "Showing the first {shown} of {total} rows; the copy and download buttons include all of them.",
  copyTable: "Copy table",
  copied: "Copied!",
  downloadCsv: "Download CSV",
  csvFile: "moving-average.csv",
  workingTitle: "How it was calculated",
  workSimple:
    "Simple average of the {k} values in each window: MAₜ = (yₜ + yₜ₋₁ + … + yₜ₋ₖ₊₁) ÷ k.",
  workWeighted: "Weighted average: MAₜ = Σ wᵢ·yᵢ ÷ Σ wᵢ over the window.",
  workExponential:
    "Exponential average: sₜ = α·yₜ + (1 − α)·sₜ₋₁ with α = {alpha}. Every past value still counts, with weights that shrink by a factor of 1 − α each period.",
  workFirst: "First average: {formula} = {value}",
} as const;

export type MovingAverageStrings = typeof MOVING_AVERAGE_CALCULATOR_STRINGS;

type SmoothingMode = "span" | "alpha";

interface Inputs {
  type: MovingAverageType;
  windowText: string;
  alignment: Alignment;
  weightsText: string;
  smoothing: SmoothingMode;
  alphaText: string;
  seed: EmaSeed;
  partial: boolean;
}

/** Turn the typed controls into options, or say which control is wrong. */
function resolveOptions(inputs: Inputs, n: number) {
  const k = parseIntegerInput(inputs.windowText);
  const alpha = parseNumberInput(inputs.alphaText);
  const weights = parseNumberList(inputs.weightsText);
  const useAlpha =
    inputs.type === "exponential" && inputs.smoothing === "alpha";
  const customWeights =
    inputs.type === "weighted" && inputs.weightsText.trim() !== "";
  const problems = {
    window: k === null || k < 1 || k > n,
    alpha: useAlpha && (alpha === null || alpha <= 0 || alpha > 1),
    weights:
      customWeights &&
      (weights.length !== k ||
        weights.some((w) => w < 0) ||
        weights.every((w) => w === 0)),
  };
  const valid = n >= 2 && k !== null && !Object.values(problems).some(Boolean);
  const options: MovingAverageOptions | null = valid
    ? {
        type: inputs.type,
        window: k,
        alignment:
          inputs.type === "exponential" ? "trailing" : inputs.alignment,
        weights: customWeights ? weights : undefined,
        alpha: useAlpha && alpha !== null ? alpha : undefined,
        emaSeed: inputs.seed,
        partial: inputs.partial,
      }
    : null;
  return { options, problems, k };
}

function problemMessage(
  n: number,
  problems: { window: boolean; alpha: boolean; weights: boolean },
  k: number | null,
  strings: MovingAverageStrings,
  fmt: (value: number) => string
): string | null {
  if (n < 2) {
    return strings.enterPrompt;
  }
  if (problems.window) {
    return t(strings.invalidWindow, { n: fmt(n) });
  }
  if (problems.alpha) {
    return strings.invalidAlpha;
  }
  if (problems.weights) {
    return t(strings.invalidWeights, { k: fmt(k ?? 0) });
  }
  return null;
}

export default function MovingAverageCalculatorWidget() {
  const strings = useToolUi(MOVING_AVERAGE_CALCULATOR_STRINGS);
  const locale = useToolLocale();
  const fmt = useToolNumber();
  const [raw, setRaw] = useState("");
  const [type, setType] = useState<MovingAverageType>("simple");
  const [windowText, setWindowText] = useState("3");
  const [alignment, setAlignment] = useState<Alignment>("trailing");
  const [weightsText, setWeightsText] = useState("");
  const [smoothing, setSmoothing] = useState<SmoothingMode>("span");
  const [alphaText, setAlphaText] = useState("0.3");
  const [seed, setSeed] = useState<EmaSeed>("first");
  const [partial, setPartial] = useState(false);

  const series = parseSeries(raw);
  const n = series.values.length;
  const { options, problems, k } = resolveOptions(
    {
      type,
      windowText,
      alignment,
      weightsText,
      smoothing,
      alphaText,
      seed,
      partial,
    },
    n
  );
  const message = problemMessage(n, problems, k, strings, fmt);

  return (
    <>
      <section className="space-y-4">
        <TextAreaField
          hint={
            <>
              {tPlural(strings.valuesRead, locale, n, { count: fmt(n) }).trim()}
              {series.skipped > 0
                ? tPlural(strings.skippedValues, locale, series.skipped, {
                    count: fmt(series.skipped),
                  })
                : null}
            </>
          }
          id="ma-data"
          label={strings.dataLabel}
          onChange={setRaw}
          placeholder={strings.placeholder}
          rows={8}
          value={raw}
        />
        <p className="text-muted-foreground text-xs">{strings.dataHint}</p>
        <div className="flex flex-wrap gap-3">
          <button
            className={BUTTON_SMALL}
            onClick={() => setRaw(strings.example)}
            type="button"
          >
            {strings.loadExample}
          </button>
          <button
            className={BUTTON_SMALL}
            onClick={() => setRaw("")}
            type="button"
          >
            {strings.clear}
          </button>
        </div>
        <PillGroup
          label={strings.typeLabel}
          name="ma-type"
          onChange={setType}
          options={[
            { value: "simple", label: strings.typeSimple },
            { value: "weighted", label: strings.typeWeighted },
            { value: "exponential", label: strings.typeExponential },
          ]}
          value={type}
        />
        <div className="grid gap-4 sm:grid-cols-2">
          <TextField
            hint={strings.windowHint}
            id="ma-window"
            label={
              type === "exponential" ? strings.spanLabel : strings.windowLabel
            }
            numeric
            onChange={setWindowText}
            value={windowText}
          />
          {type === "exponential" ? (
            <SelectField
              id="ma-seed"
              label={strings.seedLabel}
              onChange={setSeed}
              options={[
                { value: "first", label: strings.seedFirst },
                { value: "average", label: strings.seedAverage },
              ]}
              value={seed}
            />
          ) : (
            <PillGroup
              label={strings.alignmentLabel}
              name="ma-alignment"
              onChange={setAlignment}
              options={[
                { value: "trailing", label: strings.alignTrailing },
                { value: "centred", label: strings.alignCentred },
              ]}
              value={alignment}
            />
          )}
        </div>
        {type === "weighted" ? (
          <TextField
            hint={strings.weightsHint}
            id="ma-weights"
            label={strings.weightsLabel}
            numeric
            onChange={setWeightsText}
            placeholder="1, 2, 3"
            value={weightsText}
          />
        ) : null}
        {type === "exponential" ? (
          <div className="grid items-end gap-4 sm:grid-cols-2">
            <PillGroup
              label={strings.smoothingLabel}
              name="ma-smoothing"
              onChange={setSmoothing}
              options={[
                { value: "span", label: strings.smoothingSpan },
                { value: "alpha", label: strings.smoothingAlpha },
              ]}
              value={smoothing}
            />
            {smoothing === "alpha" ? (
              <TextField
                id="ma-alpha"
                label={strings.alphaLabel}
                numeric
                onChange={setAlphaText}
                value={alphaText}
              />
            ) : null}
          </div>
        ) : (
          <Checkbox
            checked={partial}
            label={strings.partial}
            onChange={setPartial}
          />
        )}
      </section>

      {message ? <Notice>{message}</Notice> : null}

      {options ? (
        <MovingAverageResults
          fmt={fmt}
          labels={series.labels}
          options={options}
          strings={strings}
          values={series.values}
        />
      ) : null}
    </>
  );
}
